import React, { useRef, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useHttp from "../../../../hooks/use-http";
import Pagination from "../../../../components/Pagination";
import * as XLSX from "xlsx";
import { AutoComplete } from "antd";
import { toast } from "react-toastify";

const FarmerBudgetReport = () => {
  const navigate = useNavigate();
  const handleButtonClick = () => {
    navigate("/farmerwise");
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0.00";
    return new Intl.NumberFormat("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const [loading, setLoading] = useState(false);

  const [employeeId] = useState();

  const [seasonlist, setSeasonlist] = useState(null);
  const [seasonId, setSeasonId] = useState("");

  const [ranchId, setRanchId] = useState("");
  const [ranchData, setRanchData] = useState([]);
  const [fieldCode, setFieldCode] = useState("");
  const [fieldOptions, setFieldOptions] = useState([]);
  const [cropId, setCropId] = useState("");
  const [cropData, setCropData] = useState([]);

  const [farmerNumber, setFarmerNumber] = useState("");
  const [farmerData, setFarmerData] = useState();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  const itemsPerPage = Number(localStorage.getItem("pageSize")) || 10;
  const [currentPage, setCurrentPage] = useState(1);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Debounced farmer number (so we don't call the API on every keystroke)
  const [debouncedFarmerNo, setDebouncedFarmerNo] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFarmerNo(farmerNumber);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [farmerNumber]);

  // Used to ignore stale responses (old request finishing after a new one)
  const requestIdRef = useRef(0);

  const [expandedGroups, setExpandedGroups] = useState({
    inputs: true,
    other: true,
  });

  // Toggle handler
  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const { sendRequest: getCropData } = useHttp();

  useEffect(() => {
    getCropData(
      {
        url: `/api/farmerCropList/?orgId=${localStorage.getItem("orgId")}`,
      },
      (data) => {
        setCropData(data.output || []);
      }
    );
  }, [getCropData]);

  const { sendRequest: getSeason } = useHttp();
  useEffect(() => {
    getSeason(
      {
        url: `/api/seasons/?orgId=${localStorage.getItem("orgId")}`,
      },
      (data) => {
        setSeasonlist(data);
      }
    );
  }, [getSeason]);

  const { sendRequest: getRanch } = useHttp();
  useEffect(() => {
    getRanch(
      {
        url: `/api/ranchDetail/?orgId=${localStorage.getItem("orgId")}`,
      },
      (data) => {
        setRanchData(data);
      }
    );
  }, [getRanch]);

  const { sendRequest: getFarmerNumber } = useHttp();
  useEffect(() => {
    var url = "";
    if (employeeId > 0) {
      url = url + "&fieldExecutiveId=" + employeeId;
    }

    if (seasonId > 0) {
      url = url + "&seasonId=" + seasonId;
    }

    if (ranchId > 0) {
      url = url + "&ranchId=" + ranchId;
    }

    getFarmerNumber(
      {
        url: `/api/budgetFarmerNumberList/?orgId=${localStorage.getItem(
          "orgId"
        )}&empId=${localStorage.getItem("empId")}${url}`,
      },
      (data) => {
        setFarmerData(data);
      }
    );
  }, [getFarmerNumber, employeeId, ranchId, seasonId]);

  const handleSearch = (value) => {
    setFarmerNumber(value);
  };
  const filteredOptions =
    farmerData &&
    farmerData
      .filter((item) => item.farmerNo.includes(farmerNumber))
      .map((item) => ({ value: item.farmerNo }));

  const { sendRequest: getBudgetData } = useHttp();
  const { sendRequest: getExportData } = useHttp();

  // Builds the filter part of the URL (shared by table and Excel export)
  const buildFilterUrl = () => {
    let urlfilter = "";
    if (employeeId > 0) urlfilter += `&fieldExecutiveId=${employeeId}`;
    if (seasonId > 0) urlfilter += `&seasonId=${seasonId}`;
    if (ranchId > 0) urlfilter += `&ranchId=${ranchId}`;
    if (fieldCode) urlfilter += `&fieldId=${fieldCode}`;
    if (debouncedFarmerNo) urlfilter += `&farmerNo=${debouncedFarmerNo}`;
    if (cropId) urlfilter += `&cropId=${cropId}`;
    return urlfilter;
  };

  const baseUrl = () =>
    `/api/farmerBudgetReport/?orgId=${localStorage.getItem(
      "orgId"
    )}&empId=${localStorage.getItem("empId")}${buildFilterUrl()}`;

  const fillBudgetData = (pageToLoad = currentPage) => {
    const myId = ++requestIdRef.current;
    setLoading(true);

    getBudgetData(
      { url: `${baseUrl()}&page=${pageToLoad}&pageSize=${itemsPerPage}` },
      (res) => {
        if (myId !== requestIdRef.current) return; // stale response, ignore
        setData(res.farmerData || []);
        setTotal(res.total || 0);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fillBudgetData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    seasonId,
    employeeId,
    ranchId,
    fieldCode,
    debouncedFarmerNo,
    cropId,
    currentPage,
  ]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setFieldOptions([]);
      return;
    }

    let filteredData = data;

    if (ranchId && ranchId > 0) {
      const selectedRanch = ranchData.find(
        (x) => String(x.ranchId) === String(ranchId)
      );

      if (selectedRanch) {
        filteredData = data.filter(
          (item) => item.ranchName === selectedRanch.ranchName
        );
      }
    }

    const uniqueFields = [
      ...new Set(filteredData.map((item) => item.fieldCode).filter(Boolean)),
    ];

    const fieldList = uniqueFields.map((f) => ({
      fieldCode: f,
    }));

    setFieldOptions(fieldList);
  }, [ranchId, ranchData, data]);

  const inputTypes = useMemo(
    () =>
      Array.from(
        new Set(data.flatMap((d) => (d.inputs || []).map((x) => x.poductType)))
      ),
    [data]
  );

  const otherTypes = useMemo(
    () =>
      Array.from(
        new Set(data.flatMap((d) => (d.other || []).map((x) => x.poductType)))
      ),
    [data]
  );

  const handleExportExcel = () => {
    if (total === 0) {
      toast.error("No data available to export!");
      return;
    }
    // No `page` param, so the API returns ALL rows for the current filters
    getExportData({ url: baseUrl() }, (res) => {
      exportToExcel(res.farmerData || []);
    });
  };

  const exportToExcel = (data) => {
    if (data.length === 0) {
      toast.error("No data available to export!");
      return;
    }

    // Column lists computed from the FULL export data
    const inputTypes = Array.from(
      new Set(data.flatMap((d) => (d.inputs || []).map((x) => x.poductType)))
    );
    const otherTypes = Array.from(
      new Set(data.flatMap((d) => (d.other || []).map((x) => x.poductType)))
    );

    const filename = "FarmerBudgetReport.xlsx";

    const headers = [
      "Farmer No",
      "Farmer Name",
      "Crop",
      "Ranch Name",
      "Field Code",
      "Plot No.",
      "Budget Template Name",
      "Season Name",
      "Executive Officer",
      "Plot Area",
      "Plot Size Unit",
    ];

    const inputHeaders = inputTypes.length
      ? inputTypes.map((t) => `Input - ${t}`)
      : ["Input -"];
    const otherHeaders = otherTypes.length
      ? otherTypes.map((t) => `Other - ${t}`)
      : ["Other -"];

    const finalHeaders = [
      ...headers,
      ...inputHeaders,
      ...otherHeaders,
      "Total Cost",
      "Harvest Cost",
      "Profit / Loss",
    ];

    const rows = data.map((itm) => {
      const row = {
        "Farmer No": itm.farmerNo ?? "-",
        "Farmer Name": itm.farmerName ?? "-",
        Crop: itm.cropNameField ?? "-",
        "Ranch Name": itm.ranchName ?? "-",
        "Field Code": itm.fieldCode ?? "-",
        "Plot No.": itm.plotName ?? "-",
        "Budget Template Name": itm.farmerBudgetTemplateName ?? "-",
        "Season Name": itm.seasonName ?? "-",
        "Executive Officer": itm.executiveName ?? "-",
        "Plot Area": itm.plotArea ?? "-",
        "Plot Size Unit": itm.plotAreaUnit ?? "-",
      };

      const inputs = Array.isArray(itm.inputs) ? itm.inputs : [];
      const others = Array.isArray(itm.other) ? itm.other : [];

      if (inputTypes.length > 0) {
        inputTypes.forEach((type) => {
          const item = inputs.find((x) => x.poductType === type);
          row[`Input - ${type}`] = item?.totalproductAmount ?? "-";
        });
      } else {
        row["Input -"] = "-";
      }

      if (otherTypes.length > 0) {
        otherTypes.forEach((type) => {
          const item = others.find((x) => x.poductType === type);
          row[`Other - ${type}`] = item?.totalproductAmount ?? "-";
        });
      } else {
        row["Other -"] = "-";
      }

      row["Total Cost"] = itm.totalcost ?? "-";
      row["Harvest Cost"] = itm.harvestCost ?? "-";
      row["Profit / Loss"] = itm.profitLoss ?? "-";

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.sheet_add_aoa(worksheet, [finalHeaders], { origin: "A1" });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Farmer Budget Report");
    XLSX.writeFile(workbook, filename);
  };

  const totalCols =
    10 + (inputTypes.length || 1) + (otherTypes.length || 1) + 3;

  return (
    <>
      <div className="content-wrapper">
        <div className="row mx-2">
          <div className="col-md-8 mt-3">
            <label className="card-title">Farmer Budget</label>
          </div>
          <div className="col-md-4 text-right mt-3">
            <button
              type="button"
              className="btn btn-tool"
              data-card-widget="remove"
              onClick={handleButtonClick}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>
        <br />
        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label>Crop Name</label>

                  <select
                    className="form-control"
                    value={cropId}
                    onChange={(e) => {
                      setCropId(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Select</option>

                    {cropData &&
                      cropData.map((x) => (
                        <option key={x.CropId} value={x.CropId}>
                          {x.CropName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label>Season</label>
                  <select
                    id="season"
                    className="form-control"
                    value={seasonId}
                    onChange={(e) => {
                      setSeasonId(e.target.value || 0);
                      setCurrentPage(1);
                    }}
                    required
                  >
                    <option value="">Select</option>
                    {seasonlist &&
                      seasonlist.map((x) => (
                        <option key={x.seasonId} value={x.seasonId}>
                          {x.seasonName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label>Ranch</label>
                  <select
                    id="ranch"
                    className="form-control"
                    value={ranchId}
                    onChange={(e) => {
                      setRanchId(e.target.value || 0);
                      setFieldCode("");
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Select</option>
                    {ranchData &&
                      ranchData.map((x) => (
                        <option key={x.ranchId} value={x.ranchId}>
                          {x.ranchName}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label>Field Name</label>
                  <select
                    className="form-control"
                    value={fieldCode}
                    onChange={(e) => {
                      setFieldCode(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Select</option>

                    {fieldOptions.map((x, i) => (
                      <option key={i} value={x.fieldCode}>
                        {x.fieldCode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <label htmlFor="farmer_number">Farmer Number</label>
                <AutoComplete
                  className="form-control"
                  style={{ height: "30px" }}
                  value={farmerNumber}
                  options={filteredOptions}
                  placeholder="Farmer Number"
                  onChange={handleSearch}
                />
              </div>
              <div className="col-md-3  text-right">
                <div className="form-group">
                  <label>&nbsp;</label>
                  <button
                    className="form-control btn btn-sm btn-primary"
                    onClick={() => {
                      if (currentPage === 1) fillBudgetData(1); // effect won't fire, so call directly
                      else setCurrentPage(1); // effect will fire
                    }}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
          <h5>Please select the parameters to get the desired result</h5>
          <hr />
        </section>

        <section className="content">
          <div className="container-fluid">
            <div className="text-right my-2">
              <button
                type="button"
                className="btn btn-sm btn-warning"
                onClick={handleExportExcel}
              >
                <i className="fas fa-file-excel"></i>
              </button>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div
                    className="card-body"
                    style={{
                      padding: "0.4rem",
                      maxHeight: "500px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-bordered text-center table-sm"
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "1200px",
                      }}
                    >
                      <thead>
                        {/* Row 1: Main Group Headers */}
                        <tr
                          className="table-header tableheaderrowview"
                          style={{
                            top: 0,
                            zIndex: 3,
                            background: "#f8f9fa",
                          }}
                        >
                          <th colSpan={10}>Farmer Details</th>

                          <th
                            colSpan={
                              expandedGroups.inputs ? inputTypes.length || 1 : 1
                            }
                            onClick={() => toggleGroup("inputs")}
                            style={{ cursor: "pointer" }}
                          >
                            Inputs {expandedGroups.inputs ? "–" : "+"}
                          </th>

                          <th
                            colSpan={
                              expandedGroups.other ? otherTypes.length || 1 : 1
                            }
                            onClick={() => toggleGroup("other")}
                            style={{ cursor: "pointer" }}
                          >
                            Other Services {expandedGroups.other ? "–" : "+"}
                          </th>

                          <th rowSpan={2}>Total Cost</th>
                          <th rowSpan={2}>Harvest Cost</th>
                          <th rowSpan={2}>Profit / Loss</th>
                        </tr>

                        {/* Row 2: Sub-Headers */}
                        <tr
                          className="table-subheader"
                          style={{
                            top: "34px", // adjust this depending on row height of first header
                            zIndex: 2,
                            background: "#f8f9fa",
                          }}
                        >
                          <th>Farmer No</th>
                          <th>Farmer Name</th>
                          <th>Crop</th>
                          <th>Field Name</th>
                          <th>Plot No.</th>
                          <th>Budget Template Name</th>
                          <th>Season Name</th>
                          <th>Executive Officer</th>
                          <th>Plot Area</th>
                          <th>Plot Size Unit</th>

                          {/* Inputs */}
                          {expandedGroups.inputs ? (
                            inputTypes.length > 0 ? (
                              inputTypes.map((type, idx) => (
                                <th key={`input-head-${idx}`}>{type}</th>
                              ))
                            ) : (
                              <th>-</th>
                            )
                          ) : (
                            <th></th>
                          )}

                          {/* Other */}
                          {expandedGroups.other ? (
                            otherTypes.length > 0 ? (
                              otherTypes.map((type, idx) => (
                                <th key={`other-head-${idx}`}>{type}</th>
                              ))
                            ) : (
                              <th>-</th>
                            )
                          ) : (
                            <th></th>
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {loading && (
                          <tr>
                            <td colSpan={totalCols} className="text-center">
                              Loading...
                            </td>
                          </tr>
                        )}
                        {!loading && data.length === 0 && (
                          <tr>
                            <td colSpan={totalCols} className="text-center">
                              No records found
                            </td>
                          </tr>
                        )}

                        {!loading &&
                          data.map((itm, idx) => {
                            const others = Array.isArray(itm.other)
                              ? itm.other
                              : [];
                            return (
                              <tr
                                key={itm.cropId}
                                className={
                                  idx % 2 === 0
                                    ? "table-row-even"
                                    : "table-row-odd"
                                }
                              >
                                <td>{itm.farmerNo || "-"}</td>
                                <td>{itm.farmerName || "-"}</td>
                                <td>{itm.cropNameField || "-"}</td>
                                <td>{itm.fieldCode || "-"}</td>
                                <td>{itm.plotName || "-"}</td>
                                <td>{itm.farmerBudgetTemplateName || "-"}</td>
                                <td>{itm.seasonName || "-"}</td>
                                <td>{itm.executiveName || "-"}</td>
                                <td>{itm.plotArea ?? "-"}</td>
                                <td>{itm.plotAreaUnit || "-"}</td>

                                {/* Inputs */}
                                {expandedGroups.inputs ? (
                                  inputTypes.length > 0 ? (
                                    inputTypes.map((type, i) => {
                                      const item = itm.inputs?.find(
                                        (x) => x.poductType === type
                                      );
                                      return (
                                        <td key={`input-${idx}-${i}`}>
                                          {item?.totalproductAmount != null
                                            ? formatNumber(
                                                item.totalproductAmount
                                              )
                                            : "-"}
                                        </td>
                                      );
                                    })
                                  ) : (
                                    <td>-</td>
                                  )
                                ) : (
                                  <td>-</td>
                                )}

                                {/* Other */}
                                {expandedGroups.other ? (
                                  otherTypes.length > 0 ? (
                                    otherTypes.map((type, i) => {
                                      const item = others.find(
                                        (x) => x.poductType === type
                                      );
                                      return (
                                        <td key={`other-${idx}-${i}`}>
                                          {item?.totalproductAmount != null
                                            ? formatNumber(
                                                item.totalproductAmount
                                              )
                                            : "-"}
                                        </td>
                                      );
                                    })
                                  ) : (
                                    <td>-</td>
                                  )
                                ) : (
                                  <td>-</td>
                                )}

                                {/* Totals */}
                                <td>
                                  {itm.totalcost != null
                                    ? formatNumber(itm.totalcost)
                                    : "-"}
                                </td>
                                <td>
                                  {itm.harvestCost != null
                                    ? formatNumber(itm.harvestCost)
                                    : "-"}
                                </td>
                                <td>
                                  {itm.profitLoss != null
                                    ? formatNumber(itm.profitLoss)
                                    : "-"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  <div className="card-footer clearfix">
                    <Pagination
                      totalItems={total}
                      itemsPerPage={itemsPerPage}
                      onPageChange={paginate}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FarmerBudgetReport;